import { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { todayIST, makeReqTracker } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileSpreadsheet, Filter, IndianRupee, CheckCircle2, Clock, AlertCircle, Users2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CLASSES = Array.from({ length: 12 }, (_, i) => String(i + 1));
const SECTIONS = ["A", "B", "C", "D"];

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function Kpi({ label, value, icon: Icon, tone }) {
  return (
    <div className={`bg-white border border-carmel-navy/10 rounded-lg p-5 shadow-card ${tone}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-carmel-navy/50">{label}</p>
          <p className="font-serif text-3xl tracking-tight text-carmel-navy mt-2">{value}</p>
        </div>
        <div className="w-9 h-9 rounded-md bg-carmel-navy/5 text-carmel-navy flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

export default function FinesPage() {
  const { user } = useAuth();
  // All authenticated users (admin/editor/viewer) can mark fines paid/pending —
  // the office accountant is often a viewer role.
  const canEdit = !!user;
  const [meta, setMeta] = useState({ categories: [], fine: { threshold: 3, amounts_by_category: {} } });
  const [totals, setTotals] = useState({ count: 0, paid: 0, pending: 0, amount_pending: 0, amount_collected: 0, threshold: 3, fine_amount: 100, amounts_by_category: {} });
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({ class_name: "", section: "", category: "", status: "" });
  const trackerRef = useRef(makeReqTracker());

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const load = async () => {
    const reqId = trackerRef.current.next();
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/fines", { params });
      if (trackerRef.current.isLatest(reqId)) {
        setRows(data.rows);
        setTotals(data.totals);
        setSelected(new Set());
      }
    } catch (e) {
      if (trackerRef.current.isLatest(reqId)) toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const keyOf = (r) => `${r.student_id}::${r.category}::${r.cycle_number}`;
  const allKeys = useMemo(() => rows.filter(r => r.status !== "active").map(keyOf), [rows]);
  const allSelected = selected.size > 0 && selected.size === allKeys.length;

  const toggleAll = () => {
    setSelected((prev) => (prev.size === allKeys.length ? new Set() : new Set(allKeys)));
  };
  const toggle = (k) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  const bulkMark = async (status) => {
    if (selected.size === 0) return toast.error("Select at least one entry");
    try {
      for (const k of selected) {
        const [student_id, category, cycle_number] = k.split("::");
        // eslint-disable-next-line no-await-in-loop
        await api.post("/fines/mark", { student_id, category, cycle_number: Number(cycle_number), status });
      }
      toast.success(`Marked ${selected.size} as ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markOne = async (r, status) => {
    try {
      await api.post("/fines/mark", { student_id: r.student_id, category: r.category, cycle_number: r.cycle_number, status });
      toast.success(`${r.student_name} cycle ${r.cycle_number} marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportExcel = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const res = await api.get(`/fines/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `carmel_fines_${todayIST()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Fines list downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell active="/fines">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-carmel-gold mb-2">Discipline & Fines</p>
          <h2 className="font-serif text-2xl md:text-4xl tracking-tight">Repeat Offenders</h2>
          <p className="text-sm text-carmel-navy/60 mt-2 max-w-2xl">
            Violations 1–3 are counted only; the <span className="font-medium text-carmel-navy">4th</span> triggers a fine (<span className="font-medium">₹250</span> Late Coming / <span className="font-medium">₹50</span> other). While a late-coming fine is unpaid, every further late day adds <span className="font-medium">₹50</span>. Marking paid closes the cycle; next violation starts fresh.
          </p>
        </div>
        <Button onClick={exportExcel} className="bg-carmel-navy hover:bg-carmel-navyMid" data-testid="fines-export-button">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Fines List
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Total Repeat Cases" value={totals.count} icon={Users2} />
        <Kpi label="Pending" value={totals.pending} icon={Clock} />
        <Kpi label="Amount to Collect" value={rupee(totals.amount_pending)} icon={AlertCircle} />
        <Kpi label="Amount Collected" value={rupee(totals.amount_collected)} icon={CheckCircle2} />
      </div>

      <div className="bg-white border border-carmel-navy/10 rounded-lg p-4 mb-6 shadow-card">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-carmel-navy/60"><Filter className="w-3.5 h-3.5" /> Filters</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Class</Label>
            <Select value={filters.class_name || "all"} onValueChange={(v) => setF("class_name", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="fines-filter-class" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Section</Label>
            <Select value={filters.section || "all"} onValueChange={(v) => setF("section", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="fines-filter-section" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={filters.category || "all"} onValueChange={(v) => setF("category", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="fines-filter-category" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All violations</SelectItem>
                {meta.categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Payment Status</Label>
            <Select value={filters.status || "all"} onValueChange={(v) => setF("status", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="fines-filter-status" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {canEdit && selected.size > 0 && (
        <div className="bg-carmel-navy text-white rounded-lg px-4 py-3 flex items-center justify-between mb-4 shadow-card" data-testid="fines-bulk-bar">
          <p className="text-sm">{selected.size} selected · {rupee(Array.from(selected).reduce((sum, k) => {
            const r = rows.find((x) => keyOf(x) === k);
            return sum + (r?.outstanding || 0);
          }, 0))}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => bulkMark("pending")} data-testid="fines-bulk-pending">Mark Pending</Button>
            <Button size="sm" className="bg-carmel-gold hover:bg-carmel-goldDark" onClick={() => bulkMark("paid")} data-testid="fines-bulk-paid">
              <IndianRupee className="w-4 h-4 mr-1" /> Mark Paid
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-carmel-navy/10 rounded-lg overflow-x-auto shadow-card">
        <Table>
          <TableHeader className="bg-carmel-slateBg">
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="fines-select-all" />
                </TableHead>
              )}
              <TableHead className="uppercase text-xs tracking-wider">Student</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Class</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Violation</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Count</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Base Fine</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Late Penalty</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Outstanding</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Status</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Marked By</TableHead>
              {canEdit && <TableHead className="uppercase text-xs tracking-wider text-right">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 10 : 8} className="text-center py-16 text-carmel-navy/50">
                  No violation cycles yet — students appear here as they accumulate violations.
                </TableCell>
              </TableRow>
            ) : rows.map((r) => {
              const k = keyOf(r);
              const isActive = r.status === "active";
              return (
                <TableRow key={k} className="hover:bg-carmel-slateBg" data-testid={`fine-row-${k}`}>
                  {canEdit && (
                    <TableCell>
                      {!isActive && (
                        <Checkbox checked={selected.has(k)} onCheckedChange={() => toggle(k)} data-testid={`fine-select-${k}`} />
                      )}
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    {r.student_name}
                    <span className="ml-2 text-[10px] text-carmel-navy/40">cycle {r.cycle_number}</span>
                  </TableCell>
                  <TableCell>{r.class_name}{r.section}</TableCell>
                  <TableCell>{r.category_label}</TableCell>
                  <TableCell className={`tabular-nums font-medium ${r.violation_count >= 4 ? "text-red-600" : "text-carmel-navy/60"}`}>
                    {Math.min(r.violation_count, 4)}/4{r.late_penalty_count > 0 ? ` +${r.late_penalty_count}` : ""}
                  </TableCell>
                  <TableCell className="tabular-nums">{r.base_fine_amount > 0 ? rupee(r.base_fine_amount) : "—"}</TableCell>
                  <TableCell className="tabular-nums">{r.late_penalty_amount > 0 ? rupee(r.late_penalty_amount) : "—"}</TableCell>
                  <TableCell className="tabular-nums font-medium">{r.outstanding > 0 ? rupee(r.outstanding) : "—"}</TableCell>
                  <TableCell>
                    {r.status === "paid" ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                      </Badge>
                    ) : r.status === "unpaid" ? (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <Clock className="w-3 h-3 mr-1" /> Unpaid
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                        Counting
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-carmel-navy/60 text-xs">
                    {r.status === "paid" ? (r.updated_by_name || "—") : "—"}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      {isActive ? (
                        <span className="text-xs text-carmel-navy/40">no fine yet</span>
                      ) : r.status === "paid" ? (
                        <Button size="sm" variant="ghost" onClick={() => markOne(r, "pending")} data-testid={`fine-unpay-${k}`}>Unmark</Button>
                      ) : (
                        <Button size="sm" className="bg-carmel-gold hover:bg-carmel-goldDark" onClick={() => markOne(r, "paid")} data-testid={`fine-pay-${k}`}>
                          <IndianRupee className="w-4 h-4 mr-1" /> Mark Paid
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </AppShell>
  );
}
